import { create } from "zustand";
import type {
  AimInput,
  KeeperReveal,
  Outcome,
  OutcomeDetail,
  PublicPuzzleParams,
  SimResult,
} from "@/game";

/**
 * State machine driving the Pitch component:
 *   AIMING    — player is dragging to set angle/power/curl
 *   COMPUTING — kick committed, waiting for /api/attempt to respond.
 *               Ball is frozen at rest, no animation. Lets us start the
 *               replay only when we have the server's keeperReveal in
 *               hand, so ball + keeper share a single clock and the dive
 *               cannot be missed by the user.
 *   REPLAYING — sim result is being animated on the canvas
 *   RESULT    — outcome shown (goal / saved / post / over / wide / wall)
 *
 * `simResult` holds the LOCAL engine output. The server's authoritative
 * outcome lands via applyServerOutcome() and overrides the locally-computed
 * fields. The keeperReveal arrives at the same time and lets the renderer
 * animate the keeper's dive accurately during the replay.
 */
export type GameState = "LOADING" | "AIMING" | "COMPUTING" | "REPLAYING" | "RESULT";

export interface PuzzleData {
  date: string;
  puzzleId: string;
  seed: number;
  params: PublicPuzzleParams;
  stadiumName: string | null;
  conditionsLabel: string | null;
}

export interface ServerOutcome {
  attemptId: string;
  outcome: Outcome;
  outcomeDetail: OutcomeDetail | null;
  score: number;
  shareCardText: string;
  streak: { current: number; longest: number };
  keeperReveal: KeeperReveal;
}

interface GameStore {
  state: GameState;
  puzzle: PuzzleData | null;
  input: AimInput | null;
  simResult: SimResult | null;
  outcome: Outcome | null;
  outcomeDetail: OutcomeDetail | null;
  /** Score mirrored to top level so the restore-existing-attempt path
   *  (which has no SimResult) can still surface it in the result overlay. */
  score: number | null;
  attemptId: string | null;
  shareCardText: string | null;
  streak: { current: number; longest: number } | null;
  keeperReveal: KeeperReveal | null;
  submitPending: boolean;
  submitError: string | null;

  setPuzzle: (p: PuzzleData) => void;
  startAiming: () => void;
  /**
   * Mark the kick as committed but DO NOT start the replay yet. State goes
   * AIMING → COMPUTING. The Pitch component shows a "syncing keeper" pip
   * while waiting for the API. Replay starts only when the API responds
   * (success or failure), via startReplayWithServerOutcome / startReplayLocal.
   */
  beginCompute: (input: AimInput) => void;
  /**
   * API responded successfully. Apply the server's canonical outcome AND
   * transition to REPLAYING in one atomic update so the Pitch RAF loop
   * sees both fields together (ball + keeper start their single clock).
   */
  startReplayWithServerOutcome: (s: ServerOutcome, sim: SimResult) => void;
  /**
   * The user already attempted today (loaded via GET /api/attempt/today
   * on page mount, OR detected via POST /api/attempt's alreadyAttempted
   * flag). Skip REPLAYING entirely — jump straight to RESULT with the
   * stored outcome. No animation, no fresh local sim. The ResultOverlay
   * surfaces the prior result + the next-puzzle countdown.
   */
  restoreExistingAttempt: (s: ServerOutcome) => void;
  /**
   * API failed (timeout, 5xx, network error). Fall back to a ball-only
   * replay using the local sim — no keeper data, ResultOverlay surfaces
   * the error, but the user still sees their kick land somewhere.
   */
  startReplayLocal: (input: AimInput, sim: SimResult, errorCode: string) => void;
  applyServerOutcome: (s: ServerOutcome) => void;
  setSubmitPending: (pending: boolean) => void;
  setSubmitError: (err: string | null) => void;
  finishReplay: () => void;
  reset: () => void;
}

const _isDebug =
  typeof window !== "undefined" &&
  new URLSearchParams(window.location.search).get("debug") === "1";

/** Wraps Zustand's setter so debug=1 sessions log every transition.
 *  Supports both signatures: set(partial) and set((prev) => partial).
 *  No-op when debug flag absent. */
type ZustandSet<T> = (
  partial: Partial<T> | ((s: T) => Partial<T>),
  replace?: boolean | undefined,
) => void;

function withDebugLog<T extends object>(set: ZustandSet<T>): ZustandSet<T> {
  if (!_isDebug) return set;
  return (partial, replace) => {
    const summary =
      typeof partial === "function"
        ? "[callback]"
        : Object.keys(partial as object);
    // eslint-disable-next-line no-console
    console.info("[curvle/store]", summary, partial);
    set(partial, replace);
  };
}

export const useGameStore = create<GameStore>((rawSet) => {
  const set = withDebugLog<GameStore>(rawSet);
  // Expose store globally under ?debug=1 for browser-console inspection.
  if (_isDebug && typeof window !== "undefined") {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setTimeout(() => { (window as any).__gameStore = useGameStore; }, 0);
  }
  return {
  state: "LOADING",
  puzzle: null,
  input: null,
  simResult: null,
  outcome: null,
  outcomeDetail: null,
  score: null,
  attemptId: null,
  shareCardText: null,
  streak: null,
  keeperReveal: null,
  submitPending: false,
  submitError: null,

  setPuzzle: (p) => set({ puzzle: p, state: "AIMING" }),
  startAiming: () =>
    set({
      state: "AIMING",
      input: null,
      simResult: null,
      outcome: null,
      outcomeDetail: null,
      score: null,
      attemptId: null,
      shareCardText: null,
      keeperReveal: null,
      submitError: null,
    }),
  beginCompute: (input) =>
    set({
      input,
      state: "COMPUTING",
      submitPending: true,
      submitError: null,
      // Don't touch outcome/keeperReveal yet — they land atomically with
      // the REPLAYING transition.
    }),
  startReplayWithServerOutcome: (s, sim) =>
    set({
      simResult: { ...sim, outcome: s.outcome, outcomeDetail: s.outcomeDetail, score: s.score },
      attemptId: s.attemptId,
      outcome: s.outcome,
      outcomeDetail: s.outcomeDetail,
      score: s.score,
      shareCardText: s.shareCardText,
      streak: s.streak,
      keeperReveal: s.keeperReveal,
      submitPending: false,
      state: "REPLAYING",
    }),
  restoreExistingAttempt: (s) =>
    set({
      attemptId: s.attemptId,
      outcome: s.outcome,
      outcomeDetail: s.outcomeDetail,
      score: s.score,
      shareCardText: s.shareCardText,
      streak: s.streak,
      keeperReveal: s.keeperReveal,
      submitPending: false,
      submitError: null,
      // No simResult / no replay — go straight to RESULT.
      state: "RESULT",
    }),
  startReplayLocal: (input, sim, errorCode) =>
    set({
      input,
      simResult: sim,
      outcome: sim.outcome,
      outcomeDetail: sim.outcomeDetail,
      score: sim.score,
      keeperReveal: null,
      submitPending: false,
      submitError: errorCode,
      state: "REPLAYING",
    }),
  // Kept for backwards compatibility (and the optional applyServerOutcome
  // mid-replay path which we don't use any more, but other call sites might).
  applyServerOutcome: (s) =>
    set((prev) => ({
      simResult: prev.simResult
        ? { ...prev.simResult, outcome: s.outcome, outcomeDetail: s.outcomeDetail, score: s.score }
        : prev.simResult,
      attemptId: s.attemptId,
      outcome: s.outcome,
      outcomeDetail: s.outcomeDetail,
      score: s.score,
      shareCardText: s.shareCardText,
      streak: s.streak,
      keeperReveal: s.keeperReveal,
    })),
  setSubmitPending: (submitPending) => set({ submitPending }),
  setSubmitError: (submitError) => set({ submitError }),
  finishReplay: () => set({ state: "RESULT" }),
  reset: () =>
    set({
      state: "AIMING",
      input: null,
      simResult: null,
      outcome: null,
      outcomeDetail: null,
      score: null,
      attemptId: null,
      shareCardText: null,
      streak: null,
      keeperReveal: null,
      submitPending: false,
      submitError: null,
    }),
  };
});
